import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, Mail, Eye, EyeOff, Activity, Loader2, ArrowRight, User, KeyRound, GraduationCap,
  BookOpen, Camera, Sparkles, Check
} from 'lucide-react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { supabase } from '../supabaseClient';

/* ============================================================
   ANIMATIONS (transform / opacity uniquement : fluide sur mobile)
   ============================================================ */
const EASE_OUT = [0.22, 1, 0.36, 1];

const stepsContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } }
};

const stepItem = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE_OUT } }
};

/* ============================================================
   CONTENU DE L'ONBOARDING (après inscription)
   ============================================================ */
const ONBOARDING_STEPS = [
  {
    icon: BookOpen,
    title: 'Choisis ton chapitre',
    text: 'Sélectionne le thème que tu veux travailler.'
  },
  {
    icon: Camera,
    title: 'Photographie ta copie',
    text: 'Prends en photo tes solutions écrites à la main.'
  },
  {
    icon: Sparkles,
    title: 'Laisse SPYRALE faire la magie',
    text: "Il lit ton écriture, traque ta première erreur… et te l'explique à voix haute, comme un prof à tes côtés.",
    highlight: true
  }
];

const getGreeting = () => (new Date().getHours() < 18 ? 'Bonjour' : 'Bonsoir');

const Authentification = ({ onLogin }) => {
  const formRef = useRef(null);
  const videoRef = useRef(null);
  const finalizeRef = useRef(null); // action de fin de connexion (stockage + navigation), déclenchée plus tard pour l'onboarding
  const navigate = useNavigate();

  // États du formulaire
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isOtpMode, setIsOtpMode] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [level, setLevel] = useState(''); // <-- NOUVEL ÉTAT POUR LA CLASSE
  const [otp, setOtp] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState('');
  const [showPlanMessage, setShowPlanMessage] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLeavingOnboarding, setIsLeavingOnboarding] = useState(false);

  // États vidéo
  const [showIntroVideo, setShowIntroVideo] = useState(false);
  const [showStartButton, setShowStartButton] = useState(false);
  const [isFadingOutVideo, setIsFadingOutVideo] = useState(false);
  const [animateFormIn, setAnimateFormIn] = useState(false);

  const LOGO_URL = "https://res.cloudinary.com/do5zemcrk/image/upload/v1766679468/Gemini_Generated_Image_35upzx35upzx35up_1_farsag.png";
  const VIDEO_URL = "https://res.cloudinary.com/do5zemcrk/video/upload/v1791982013/download_eba01f.mp4";

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      setShowIntroVideo(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.play().catch(() => {});
      }, 100);
      const buttonTimer = setTimeout(() => setShowStartButton(true), 5000);
      return () => clearTimeout(buttonTimer);
    } else {
      setAnimateFormIn(true);
    }
  }, []);

  const handleStartSession = () => {
    setIsFadingOutVideo(true);
    setTimeout(() => {
      setShowIntroVideo(false);
      setTimeout(() => setAnimateFormIn(true), 50);
    }, 500);
  };

  /* Fin de connexion élève : stockage de la session puis navigation */
  const finalizeStudentLogin = useCallback((userData, token) => {
    // Formatage des données pour le reste de l'application
    const userInfoFormatted = {
      id: userData.id,
      email: userData.email,
      firstName: userData.first_name,
      lastName: userData.last_name,
      plan: userData.plan,
      role: userData.role,
      level: userData.level || 'Non défini',
      classroom_id: userData.classroom_id
    };

    sessionStorage.setItem('user_info', JSON.stringify(userInfoFormatted));
    localStorage.setItem('token', token);

    if (onLogin) onLogin();
    navigate(userData.role === 'student_partner' ? '/mes-devoirs' : '/smartex-chap');
  }, [navigate, onLogin]);

  const handleAuthSuccess = async (session, user, isSignup = false) => {
    try {
      const { data: userData, error: dbError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (dbError) throw dbError;

      const role = userData.role;
      const token = session.access_token;

      setUserName(userData.first_name);

      // Nouvel élève : on affiche l'onboarding, la connexion se termine au clic sur « C'est parti »
      if (isSignup && role !== 'teacher' && role !== 'admin') {
        finalizeRef.current = () => finalizeStudentLogin(userData, token);
        setShowOnboarding(true);
        return;
      }

      setShowPlanMessage(true);

      setTimeout(() => {
        if (role === 'teacher' || role === 'admin') {
          window.location.href = `https://spyrale-professor-portal-production.up.railway.app/dashboard?token=${token}`;
        } else {
          finalizeStudentLogin(userData, token);
        }
      }, 1800);
    } catch (err) {
      setError("Erreur lors de la récupération du profil utilisateur.");
      setLoading(false);
    }
  };

  const handleStartLearning = () => {
    if (isLeavingOnboarding) return;
    setIsLeavingOnboarding(true);
    // Petit délai pour laisser jouer le retour visuel du bouton
    setTimeout(() => {
      if (finalizeRef.current) finalizeRef.current();
    }, 250);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isOtpMode) {
        if (!otp) {
          setError('Veuillez saisir le code à 6 chiffres');
          setLoading(false);
          return;
        }
        const { data, error: verifyError } = await supabase.auth.verifyOtp({
          email,
          token: otp,
          type: 'signup'
        });

        if (verifyError) throw verifyError;
        if (data.session && data.user) {
          await handleAuthSuccess(data.session, data.user, true);
        }

      } else if (isLoginMode) {
        if (!email || !password) {
          setError('Veuillez remplir tous les champs');
          setLoading(false);
          return;
        }
        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (signInError) throw new Error('Identifiants invalides ou adresse non confirmée');
        await handleAuthSuccess(data.session, data.user, false);

      } else {
        // VÉRIFICATION AJOUTÉE POUR LA CLASSE
        if (!email || !password || !firstName || !lastName || !level) {
          setError('Veuillez remplir tous les champs et choisir votre classe');
          setLoading(false);
          return;
        }

        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName,
              level: level // <-- ENVOI DE LA CLASSE DANS LES MÉTADONNÉES
            }
          }
        });

        if (signUpError) {
          if (signUpError.message.includes('already registered')) {
            throw new Error('Cette adresse email est déjà utilisée.');
          }
          throw signUpError;
        }
        setIsOtpMode(true);
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
      setLoading(false);
    }
  };

  /* ============================================================
     ÉCRAN 1 : vidéo d'intro (mobile)
     ============================================================ */
  if (showIntroVideo) {
    return (
      <div className={`fixed inset-0 bg-black z-[9999] flex items-center justify-center transition-opacity duration-500 ease-in-out ${isFadingOutVideo ? 'opacity-0' : 'opacity-100'}`}>
        <video ref={videoRef} src={VIDEO_URL} autoPlay playsInline muted loop className="w-full h-full object-cover select-none pointer-events-none opacity-80" />
        <div
          className={`absolute left-0 right-0 px-6 flex justify-center transition-all duration-1000 ease-out transform ${showStartButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'}`}
          style={{ bottom: 'max(4rem, calc(env(safe-area-inset-bottom) + 1.5rem))' }}
        >
          <button onClick={handleStartSession} className="group flex w-full max-w-sm justify-center items-center gap-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-4 py-4 text-base font-bold text-white shadow-2xl hover:bg-white/20 active:scale-95 transition-all duration-300 touch-manipulation">
            Démarrer ma session
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    );
  }

  /* ============================================================
     ÉCRAN 2 : onboarding après inscription
     ============================================================ */
  if (showOnboarding) {
    return (
      <MotionConfig reducedMotion="user">
        <div
          className="min-h-[100dvh] bg-slate-50 flex flex-col justify-center items-center px-4 font-sans overflow-hidden relative"
          style={{
            paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
            paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))'
          }}
        >
          {/* Lueur statique (aucun blur animé : gratuit pour le GPU) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(circle at 50% 18%, rgba(79,70,229,0.14) 0%, transparent 55%)' }}
          />

          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={isLeavingOnboarding ? { opacity: 0, y: -12, scale: 0.98 } : { opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: isLeavingOnboarding ? 0.25 : 0.5, ease: EASE_OUT }}
            className="relative z-10 w-full max-w-md bg-white rounded-[28px] shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 px-6 sm:px-8 pt-8 pb-6"
            role="dialog"
            aria-label="Bienvenue sur SPYRALE"
          >
            {/* En-tête */}
            <div className="text-center">
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', damping: 18, stiffness: 260, delay: 0.1 }}
                className="w-14 h-14 mx-auto mb-5"
              >
                <img src={LOGO_URL} alt="Logo Spyrale" className="w-full h-full object-contain drop-shadow-sm" />
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.2 }}
                className="text-[28px] font-black tracking-tight text-slate-900 leading-[1.1]"
              >
                Bienvenue,{' '}
                <span className="bg-gradient-to-r from-indigo-600 to-violet-500 bg-clip-text text-transparent">
                  {userName}
                </span>{' '}
                !
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.27 }}
                className="mt-2 text-sm text-slate-500 font-medium"
              >
                Ton compte est prêt. Voici comment progresser en 3 gestes.
              </motion.p>
            </div>

            {/* Parcours : une ligne qui se dessine, 3 pastilles numérotées, zéro icône */}
            <motion.ol
              variants={stepsContainer}
              initial="hidden"
              animate="visible"
              className="mt-8 text-left"
            >
              {ONBOARDING_STEPS.map((step, index) => {
                const isLast = index === ONBOARDING_STEPS.length - 1;
                return (
                  <motion.li
                    key={step.title}
                    variants={stepItem}
                    className={`relative flex items-start gap-4 ${isLast ? '' : 'pb-7'}`}
                  >
                    {/* Pastille numérotée */}
                    <span
                      className={`relative z-10 grid h-10 w-10 shrink-0 place-items-center rounded-full text-[15px] font-black tabular-nums ${
                        step.highlight
                          ? 'bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-600/30'
                          : 'bg-indigo-50 text-indigo-600 ring-2 ring-indigo-100'
                      }`}
                    >
                      {step.highlight && (
                        <motion.span
                          aria-hidden="true"
                          className="absolute inset-0 rounded-full bg-indigo-500"
                          animate={{ scale: [1, 1.5], opacity: [0.35, 0] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: 1.4 }}
                        />
                      )}
                      <span className="relative">{index + 1}</span>
                    </span>

                    {/* Trait qui se dessine vers l'étape suivante */}
                    {!isLast && (
                      <motion.span
                        aria-hidden="true"
                        initial={{ scaleY: 0 }}
                        animate={{ scaleY: 1 }}
                        transition={{ duration: 0.45, ease: EASE_OUT, delay: 0.55 + index * 0.3 }}
                        className="absolute left-[19px] top-10 bottom-0 w-[2px] origin-top rounded-full bg-gradient-to-b from-indigo-300 to-indigo-100"
                      />
                    )}

                    {/* Texte */}
                    <span className="min-w-0 flex-1 pt-1.5">
                      <span
                        className={`block text-[17px] font-extrabold leading-tight tracking-tight ${
                          step.highlight
                            ? 'bg-gradient-to-r from-indigo-600 to-violet-500 bg-clip-text text-transparent'
                            : 'text-slate-900'
                        }`}
                      >
                        {step.title}
                      </span>
                      <span className="block mt-1 text-[13.5px] text-slate-500 leading-snug">{step.text}</span>
                    </span>
                  </motion.li>
                );
              })}
            </motion.ol>

            {/* Bouton */}
            <motion.button
              type="button"
              onClick={handleStartLearning}
              disabled={isLeavingOnboarding}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: EASE_OUT, delay: 1.1 }}
              whileTap={{ scale: 0.97 }}
              className="mt-8 flex w-full min-h-[56px] justify-center items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 active:brightness-95 px-4 text-base font-bold text-white shadow-lg shadow-indigo-600/25 touch-manipulation disabled:opacity-80"
            >
              {isLeavingOnboarding ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  C'est parti
                  <motion.span
                    aria-hidden="true"
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay: 1.6 }}
                    className="flex"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </motion.span>
                </>
              )}
            </motion.button>
          </motion.div>
        </div>
      </MotionConfig>
    );
  }

  /* ============================================================
     ÉCRAN 3 : bienvenue après connexion
     ============================================================ */
  if (showPlanMessage) {
    return (
      <MotionConfig reducedMotion="user">
        <div
          className="min-h-[100dvh] bg-slate-50 flex flex-col justify-center items-center px-4 font-sans overflow-hidden relative"
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(circle at 50% 38%, rgba(79,70,229,0.10) 0%, transparent 55%)' }}
          />

          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, ease: EASE_OUT }}
            className="relative z-10 w-full max-w-sm bg-white rounded-[28px] shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 px-8 py-10 text-center"
            role="status"
            aria-live="polite"
          >
            {/* Logo + pastille de validation */}
            <div className="relative w-16 h-16 mx-auto mb-6">
              <motion.img
                src={LOGO_URL}
                alt="Logo Spyrale"
                className="w-full h-full object-contain"
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', damping: 16, stiffness: 240, delay: 0.1 }}
              />
              <motion.span
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-emerald-500 border-[3px] border-white flex items-center justify-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 12, stiffness: 320, delay: 0.5 }}
              >
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3.5} />
              </motion.span>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.25 }}
              className="text-[11px] font-bold uppercase tracking-widest text-emerald-600"
            >
              Connexion réussie
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.32 }}
              className="mt-2 text-2xl font-black tracking-tight text-slate-900 leading-tight"
            >
              {getGreeting()}, {userName} !
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.4 }}
              className="mt-1.5 text-sm text-slate-500 font-medium"
            >
              Content de te revoir. On reprend là où tu t'étais arrêté.
            </motion.p>

            {/* Barre de progression : donne un rythme à l'attente */}
            <div className="mt-8 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <motion.div
                className="h-full w-full origin-left rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1.6, ease: 'easeInOut', delay: 0.2 }}
              />
            </div>
            <p className="mt-3 text-xs font-medium text-slate-400">Préparation de ton espace…</p>
          </motion.div>
        </div>
      </MotionConfig>
    );
  }

  /* ============================================================
     ÉCRAN 4 : formulaire (connexion / inscription / code)
     ============================================================ */
  return (
    <MotionConfig reducedMotion="user">
      <div
        className="min-h-[100dvh] bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans overflow-hidden"
        style={{ paddingTop: 'max(3rem, env(safe-area-inset-top))', paddingBottom: 'max(3rem, env(safe-area-inset-bottom))' }}
      >
        <div className={`transition-all duration-700 ease-out transform ${animateFormIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="sm:mx-auto sm:w-full sm:max-w-md text-center px-4">
            <div className="inline-flex items-center justify-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Logo Spyrale" className="w-10 h-10 object-contain drop-shadow-sm" />
              <span className="text-3xl font-black text-slate-900 tracking-tight mt-1">SPYRALE ULTIMATE</span>
            </div>

            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={isOtpMode ? 'otp' : isLoginMode ? 'login' : 'signup'}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
              >
                <h2 className="text-2xl font-bold tracking-tight text-slate-900">
                  {isOtpMode ? 'Vérification de l\'email' : (isLoginMode ? 'Connexion à votre espace' : 'Créer un compte élève gratuitement')}
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  {isOtpMode ? 'Un code à 6 chiffres a été envoyé.' : 'Portail enseignant & élève partenaire'}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div ref={formRef} className="bg-white py-8 px-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:rounded-2xl rounded-2xl sm:px-10 border border-slate-100 sm:mx-0 mx-4 transition-all">

              {!isOtpMode && (
                <div className="relative flex p-1 bg-slate-100 rounded-xl mb-6">
                  {/* Pastille qui glisse d'un onglet à l'autre */}
                  <motion.span
                    aria-hidden="true"
                    className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-lg bg-white shadow-sm"
                    animate={{ x: isLoginMode ? 0 : '100%' }}
                    transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                  />
                  <button type="button" onClick={() => { setIsLoginMode(true); setError(''); }} className={`relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors touch-manipulation ${isLoginMode ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}>
                    Se connecter
                  </button>
                  <button type="button" onClick={() => { setIsLoginMode(false); setError(''); }} className={`relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors touch-manipulation ${!isLoginMode ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}>
                    S'inscrire
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {isOtpMode ? (
                  <div>
                    <label className="block text-sm font-semibold leading-6 text-slate-900">Code de vérification</label>
                    <div className="mt-2 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <KeyRound className="h-5 w-5 text-slate-400" />
                      </div>
                      <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength="6" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} disabled={loading} className="block w-full rounded-xl border-0 py-3.5 pl-10 pr-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm text-center tracking-widest text-lg font-bold" placeholder="000000" />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Champs d'inscription : déploiement fluide au lieu d'un saut brutal */}
                    <AnimatePresence initial={false}>
                      {!isLoginMode && (
                        <motion.div
                          key="signup-fields"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28, ease: EASE_OUT }}
                          className="overflow-hidden -mx-1 px-1"
                        >
                          <div className="space-y-6 pb-6 pt-1">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-semibold leading-6 text-slate-900">Prénom</label>
                                <div className="mt-2 relative">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-slate-400" />
                                  </div>
                                  <input type="text" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={loading} className="block w-full rounded-xl border-0 py-3.5 pl-10 pr-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-indigo-600 sm:text-sm" placeholder="Jean" />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-semibold leading-6 text-slate-900">Nom</label>
                                <div className="mt-2">
                                  <input type="text" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={loading} className="block w-full rounded-xl border-0 py-3.5 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-indigo-600 sm:text-sm" placeholder="Dupont" />
                                </div>
                              </div>
                            </div>

                            {/* --- SÉLECTEUR DE CLASSE --- */}
                            <div>
                              <label className="block text-sm font-semibold leading-6 text-slate-900">Classe</label>
                              <div className="mt-2 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <GraduationCap className="h-5 w-5 text-slate-400" />
                                </div>
                                <select
                                  value={level}
                                  onChange={(e) => setLevel(e.target.value)}
                                  disabled={loading}
                                  className="block w-full rounded-xl border-0 py-3.5 pl-10 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-indigo-600 sm:text-sm appearance-none bg-white cursor-pointer"
                                >
                                  <option value="" disabled>Sélectionnez votre classe</option>
                                  <option value="Sixième">Sixième</option>
                                  <option value="Cinquième">Cinquième</option>
                                  <option value="Quatrième">Quatrième</option>
                                  <option value="Troisième">Troisième</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div>
                      <label className="block text-sm font-semibold leading-6 text-slate-900">Adresse email</label>
                      <div className="mt-2 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-slate-400" />
                        </div>
                        <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} className="block w-full rounded-xl border-0 py-3.5 pl-10 pr-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm" placeholder="nom@ecole.fr" />
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="block text-sm font-semibold leading-6 text-slate-900">Mot de passe</label>
                      <div className="mt-2 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-slate-400" />
                        </div>
                        <input type={showPassword ? "text" : "password"} autoComplete={isLoginMode ? 'current-password' : 'new-password'} value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} className="block w-full rounded-xl border-0 py-3.5 pl-10 pr-12 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm" placeholder="••••••••" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'} className="absolute inset-y-0 right-0 w-12 flex items-center justify-center text-slate-400 hover:text-slate-600 touch-manipulation">
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <AnimatePresence initial={false}>
                  {error && (
                    <motion.div
                      key="auth-error"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: EASE_OUT }}
                      className="overflow-hidden"
                      role="alert"
                    >
                      <div className="mt-6 rounded-xl bg-red-50 p-4 flex items-start gap-3">
                        <Activity className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
                        <p className="text-sm font-medium text-red-800">{error}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <button type="submit" disabled={loading} className="mt-6 flex w-full min-h-[52px] justify-center items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] px-3 py-3.5 text-sm font-bold text-white shadow-sm transition-all duration-200 touch-manipulation disabled:opacity-80">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (isOtpMode ? "Valider le code" : (isLoginMode ? "Se connecter" : "Découvre Alex & Chloé"))}
                </button>
              </form>

              {isOtpMode && (
                <div className="mt-4 text-center">
                  <button type="button" onClick={() => setIsOtpMode(false)} className="text-sm text-indigo-600 font-medium hover:underline min-h-[44px] touch-manipulation">
                    Modifier l'adresse email
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MotionConfig>
  );
};

export default Authentification;